import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, MessageCircle, CalendarPlus, Check, TriangleAlert, X } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import type { Theme } from '@/types/theme'
import { useStartZephChat } from '@/features/dashboard/hooks/useStartZephChat'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { getCalendarOptions, setMainCalendarProvider, createBooking } from '../services/bookingsApi'
import BookingDateTimePicker, { formatValueLabel } from './BookingDateTimePicker'
import type { Booking, BookingDurationMinutes, CalendarChoice, CalendarOptions, CreateBookingInput } from '../types/index.js'

type Props = {
  workspaceId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onBooked: () => void
}

type ModalView = 'form' | 'success' | 'degraded'

type ErrorInfo = { title: string; body?: string }

type CalendarProviderKind = 'google' | 'outlook'

const CHOICE_LABEL: Record<CalendarChoice, string> = { google: 'Google', outlook: 'Outlook', both: 'Both' }
const PROVIDER_LABEL: Record<CalendarProviderKind, string> = { google: 'Google Calendar', outlook: 'Outlook Calendar' }
const DURATION_OPTIONS: BookingDurationMinutes[] = [15, 30, 45, 60]
const EMAIL_RE = /^[^\s<>,]+@[^\s<>,]+\.[^\s<>,]+$/

// Backend throws AppError(409, 'slot_unavailable', 'That slot is no longer
// available — pick another time.') for a booking conflict (see
// apps/api/src/features/bookings/service.ts) — the frontend HTTP client only
// surfaces the message, not the code, so this matches on message text to
// split it into the designed title+body banner shape.
function parseBookingError(err: unknown, fallback: string): ErrorInfo {
  const message = err instanceof Error ? err.message : fallback
  if (message.toLowerCase().includes('slot is no longer available')) {
    return { title: 'That slot is no longer available', body: 'Pick another time and try again.' }
  }
  return { title: message || fallback }
}

const BookMeetingModal: React.FC<Props> = ({ workspaceId, open, onOpenChange, onBooked }) => {
  const { currentTheme: t } = useTheme()
  const startChat = useStartZephChat()

  const [options, setOptions] = useState<CalendarOptions | null>(null)
  const [loadingOptions, setLoadingOptions] = useState(true)
  const [choice, setChoice] = useState<CalendarChoice | null>(null)
  const [rememberChoice, setRememberChoice] = useState(false)

  const [title, setTitle] = useState('')
  const [attendeeEmail, setAttendeeEmail] = useState('')
  const [description, setDescription] = useState('')
  const [start, setStart] = useState<Date | null>(null)
  const [duration, setDuration] = useState<BookingDurationMinutes>(30)
  const [isOnlineMeeting, setIsOnlineMeeting] = useState(false)
  const [notes, setNotes] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<ErrorInfo | null>(null)

  const [view, setView] = useState<ModalView>('form')
  const [booking, setBooking] = useState<Booking | null>(null)
  const [retrying, setRetrying] = useState(false)
  const [retryError, setRetryError] = useState<ErrorInfo | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoadingOptions(true)
    setError(null)
    getCalendarOptions(workspaceId)
      .then((opts) => {
        if (cancelled) return
        setOptions(opts)
        setChoice(opts.savedDefault ?? (opts.googleConnected && !opts.outlookConnected ? 'google' : opts.outlookConnected && !opts.googleConnected ? 'outlook' : null))
      })
      .catch((err) => { if (!cancelled) setError({ title: err instanceof Error ? err.message : 'Could not load your calendars.' }) })
      .finally(() => { if (!cancelled) setLoadingOptions(false) })
    return () => { cancelled = true }
  }, [open, workspaceId])

  const resetForm = useCallback(() => {
    setTitle('')
    setAttendeeEmail('')
    setDescription('')
    setStart(null)
    setDuration(30)
    setIsOnlineMeeting(false)
    setNotes('')
    setRememberChoice(false)
    setError(null)
    setView('form')
    setBooking(null)
    setRetrying(false)
    setRetryError(null)
  }, [])

  const handleOpenChange = useCallback((next: boolean) => {
    if (!next) resetForm()
    onOpenChange(next)
  }, [onOpenChange, resetForm])

  const bothConnected = !!options?.googleConnected && !!options?.outlookConnected
  const noneConnected = !!options && !options.googleConnected && !options.outlookConnected

  const buildBookingInput = useCallback((calendarChoiceOverride?: CalendarChoice): CreateBookingInput => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const effectiveChoice = calendarChoiceOverride ?? choice
    return {
      title: title.trim(),
      attendeeEmail: attendeeEmail.trim(),
      startIso: (start as Date).toISOString(),
      durationMinutes: duration,
      timeZone,
      isOnlineMeeting,
      ...(description.trim() ? { description: description.trim() } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
      ...(effectiveChoice ? { calendarChoice: effectiveChoice } : {}),
    }
  }, [title, attendeeEmail, start, duration, isOnlineMeeting, description, notes, choice])

  const handleSubmit = useCallback(async () => {
    if (!title.trim() || !attendeeEmail.trim() || !start) {
      setError({ title: 'Fill in a title, attendee email, and a date & time.' })
      return
    }
    if (!EMAIL_RE.test(attendeeEmail.trim())) {
      setError({ title: 'Enter a valid email address.' })
      return
    }
    if (bothConnected && !choice) {
      setError({ title: 'Pick which calendar to book this to.' })
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      if (rememberChoice && choice) {
        await setMainCalendarProvider(workspaceId, choice).catch(() => {})
      }
      const result = await createBooking(workspaceId, buildBookingInput())
      setBooking(result)
      setView(result.degraded ? 'degraded' : 'success')
      onBooked()
    } catch (err) {
      setError(parseBookingError(err, 'Could not book the meeting. Please try again.'))
    } finally {
      setSubmitting(false)
    }
  }, [title, attendeeEmail, start, choice, bothConnected, rememberChoice, workspaceId, buildBookingInput, onBooked])

  const succeededProviders = booking?.calendarRefs.map((r) => r.provider) ?? []
  const failedProvider: CalendarProviderKind | undefined = (['google', 'outlook'] as const).find((p) => !succeededProviders.includes(p))

  const handleRetry = useCallback(async () => {
    if (!failedProvider) return
    setRetrying(true)
    setRetryError(null)
    try {
      const result = await createBooking(workspaceId, buildBookingInput(failedProvider))
      setBooking((prev) => (prev ? { ...result, calendarRefs: [...prev.calendarRefs, ...result.calendarRefs] } : result))
      setView('success')
      onBooked()
    } catch (err) {
      setRetryError(parseBookingError(err, 'Could not book the meeting. Please try again.'))
    } finally {
      setRetrying(false)
    }
  }, [failedProvider, workspaceId, buildBookingInput, onBooked])

  const handleStartChat = useCallback(() => {
    handleOpenChange(false)
    startChat({
      seedKey: `book-meeting-${Date.now()}`,
      title: 'Book a meeting',
      briefing: 'I want to book a meeting — ask me who it’s with and when, then book it on my calendar.',
    })
  }, [handleOpenChange, startChat])

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="rounded-2xl border-none max-w-md w-full" style={{ background: t.secondary, color: t.text }}>
        <DialogHeader>
          <DialogTitle style={{ color: t.text }}>Book a meeting</DialogTitle>
        </DialogHeader>

        {loadingOptions ? (
          <div className="flex items-center justify-center" style={{ height: 160 }}>
            <Loader2 size={22} className="animate-spin" style={{ color: t.mutedText }} />
          </div>
        ) : noneConnected ? (
          <div className="flex flex-col items-center text-center gap-3.5" style={{ padding: '8px 24px 24px 24px' }}>
            <div className="flex items-center justify-center rounded-full" style={{ width: 48, height: 48, backgroundColor: t.hoverBackground }}>
              <CalendarPlus size={22} style={{ color: t.mutedText }} />
            </div>
            <p className="text-[15px] font-bold" style={{ color: t.text }}>Connect a calendar first</p>
            <p className="text-xs" style={{ color: t.mutedText, maxWidth: 320 }}>
              You need Google or Outlook connected before you can book a meeting.
            </p>
            <Link
              to="/dashboard/settings?tab=integrations"
              className="rounded-lg font-medium px-4 py-2 text-sm text-white"
              style={{ backgroundColor: t.accent }}
            >
              Go to Integrations
            </Link>
          </div>
        ) : view === 'success' || view === 'degraded' ? (
          <BookingResultView
            t={t}
            view={view}
            booking={booking}
            title={title}
            attendeeEmail={attendeeEmail}
            start={start}
            failedProvider={failedProvider}
            retryError={retryError}
          />
        ) : (
          <div className="flex flex-col gap-4">
            <Field label="Title" value={title} onChange={setTitle} placeholder="Intro call" t={t} />
            <Field label="Email" value={attendeeEmail} onChange={setAttendeeEmail} placeholder="jane@prospect.com" type="email" t={t} />

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: t.mutedText }}>Description</label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What's this meeting about? Visible on the calendar invite."
                rows={2}
                style={{ backgroundColor: t.inputBackground, borderColor: t.border, color: t.text }}
              />
            </div>

            <div className="flex flex-col gap-3">
              <BookingDateTimePicker
                value={start}
                onChange={setStart}
                durationMinutes={duration}
                workspaceId={workspaceId}
                calendarChoice={choice}
                googleConnected={!!options?.googleConnected}
                outlookConnected={!!options?.outlookConnected}
              />
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: t.mutedText }}>Duration</label>
                <div className="flex gap-1.5">
                  {DURATION_OPTIONS.map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setDuration(d)}
                      className="flex-1 text-xs font-medium py-2 rounded-md border transition-colors"
                      style={{
                        borderColor: duration === d ? t.accent : t.border,
                        backgroundColor: duration === d ? t.hoverBackground : 'transparent',
                        color: t.text,
                      }}
                    >
                      {d}m
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="flex items-center gap-2 text-xs" style={{ color: t.text }}>
                <input type="checkbox" checked={isOnlineMeeting} onChange={(e) => setIsOnlineMeeting(e.target.checked)} />
                Make it an online meeting
              </label>
              <p className="text-[11px] pl-5" style={{ color: t.mutedText }}>Adds a video link to the invite.</p>
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <label className="text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: t.mutedText }}>Notes</label>
                <span
                  className="text-[10px] font-medium px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: t.sectionBackground, color: t.mutedText, border: `1px solid ${t.border}` }}
                >
                  Only visible to you
                </span>
              </div>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Private prep notes — never sent to the invitee."
                rows={2}
                style={{ backgroundColor: t.inputBackground, borderColor: t.border, color: t.text }}
              />
            </div>

            {bothConnected && (
              <div className="flex flex-col gap-2">
                <label className="text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: t.mutedText }}>
                  Which calendar?
                </label>
                <div className="flex gap-2">
                  {(['google', 'outlook', 'both'] as CalendarChoice[]).map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setChoice(c)}
                      className="flex-1 text-xs font-medium py-2 rounded-md border transition-colors"
                      style={{
                        borderColor: choice === c ? t.accent : t.border,
                        backgroundColor: choice === c ? t.hoverBackground : 'transparent',
                        color: t.text,
                      }}
                    >
                      {CHOICE_LABEL[c]}
                    </button>
                  ))}
                </div>
                <label className="flex items-center gap-2 text-xs mt-1" style={{ color: t.mutedText }}>
                  <input type="checkbox" checked={rememberChoice} onChange={(e) => setRememberChoice(e.target.checked)} />
                  Remember this as my default
                </label>
              </div>
            )}

            {error && <InlineErrorBanner error={error} t={t} />}

            <div className="rounded-xl p-3 flex items-center justify-between gap-3" style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}>
              <p className="text-xs" style={{ color: t.mutedText }}>You can also create a booking with chat.</p>
              <button
                type="button"
                onClick={handleStartChat}
                className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md shrink-0"
                style={{ border: `1px solid ${t.border}`, color: t.text }}
              >
                <MessageCircle size={13} />
                Start chat
              </button>
            </div>
          </div>
        )}

        {!loadingOptions && !noneConnected && view === 'form' && (
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => handleOpenChange(false)}
              className="rounded-lg font-medium h-auto px-4 py-2 text-sm"
              style={{ background: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleSubmit()}
              disabled={submitting}
              className="rounded-lg font-medium h-auto px-4 py-2 text-sm"
              style={{ backgroundColor: t.accent, color: '#fff' }}
            >
              {submitting ? 'Booking…' : 'Book meeting'}
            </Button>
          </DialogFooter>
        )}

        {!loadingOptions && !noneConnected && view === 'success' && (
          <DialogFooter>
            <Button
              onClick={() => handleOpenChange(false)}
              className="rounded-lg font-medium h-auto px-4 py-2 text-sm"
              style={{ backgroundColor: t.accent, color: '#fff' }}
            >
              Done
            </Button>
          </DialogFooter>
        )}

        {!loadingOptions && !noneConnected && view === 'degraded' && (
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => void handleRetry()}
              disabled={retrying}
              className="rounded-lg font-medium h-auto px-4 py-2 text-sm"
              style={{ background: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
            >
              {retrying ? 'Retrying…' : `Retry ${failedProvider ? PROVIDER_LABEL[failedProvider].replace(' Calendar', '') : ''}`}
            </Button>
            <Button
              onClick={() => handleOpenChange(false)}
              className="rounded-lg font-medium h-auto px-4 py-2 text-sm"
              style={{ backgroundColor: t.accent, color: '#fff' }}
            >
              Done
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}

const Field: React.FC<{
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  type?: string
  t: Theme
}> = ({ label, value, onChange, placeholder, type, t }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: t.mutedText }}>{label}</label>
    <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={{ backgroundColor: t.inputBackground, borderColor: t.border, color: t.text }} />
  </div>
)

const InlineErrorBanner: React.FC<{ error: ErrorInfo; t: Theme }> = ({ error, t }) => (
  <div className="flex items-start gap-2.5 rounded-[10px]" style={{ backgroundColor: t.errorBackground, padding: '12px 14px' }}>
    <TriangleAlert size={16} className="shrink-0 mt-0.5" style={{ color: t.errorText }} />
    <div className="flex flex-col gap-0.5">
      <span className="text-[13px] font-bold" style={{ color: t.errorText }}>{error.title}</span>
      {error.body && <span className="text-xs" style={{ color: t.errorText }}>{error.body}</span>}
    </div>
  </div>
)

const BookingResultView: React.FC<{
  t: Theme
  view: 'success' | 'degraded'
  booking: Booking | null
  title: string
  attendeeEmail: string
  start: Date | null
  failedProvider?: CalendarProviderKind
  retryError: ErrorInfo | null
}> = ({ t, view, booking, title, attendeeEmail, start, failedProvider, retryError }) => {
  const degraded = view === 'degraded'
  const iconBackground = degraded ? t.warningBackground : t.successBackground
  const iconColor = degraded ? t.warningText : t.successText

  return (
    <div className="flex flex-col items-center text-center" style={{ gap: 16, padding: '28px 24px 8px 24px' }}>
      <div className="flex items-center justify-center rounded-full" style={{ width: 56, height: 56, backgroundColor: iconBackground }}>
        {degraded ? <TriangleAlert size={26} style={{ color: iconColor }} /> : <Check size={26} style={{ color: iconColor }} />}
      </div>
      <p className="text-[17px] font-bold" style={{ color: t.text }}>{degraded ? 'Booked — but only partly' : 'Meeting booked'}</p>
      <p className="text-xs" style={{ color: t.mutedText, maxWidth: 340 }}>
        {title} with {attendeeEmail} · {start ? formatValueLabel(start) : ''}
      </p>

      <div className="flex flex-col gap-2 w-full">
        {(booking?.calendarRefs ?? []).map((ref) => (
          <div
            key={ref.provider}
            className="flex items-center text-left"
            style={{ backgroundColor: t.hoverBackground, borderRadius: 10, padding: '10px 12px', gap: 10 }}
          >
            <div className="flex items-center justify-center rounded-full shrink-0" style={{ width: 26, height: 26, backgroundColor: t.successBackground }}>
              <Check size={14} style={{ color: t.successText }} />
            </div>
            <div className="flex flex-col">
              <span className="text-[13px] font-bold" style={{ color: t.text }}>{PROVIDER_LABEL[ref.provider]}</span>
              <span className="text-[11px]" style={{ color: t.mutedText }}>Confirmed and synced</span>
            </div>
          </div>
        ))}

        {degraded && failedProvider && (
          <div
            className="flex items-center text-left"
            style={{ backgroundColor: t.hoverBackground, borderRadius: 10, padding: '10px 12px', gap: 10 }}
          >
            <div className="flex items-center justify-center rounded-full shrink-0" style={{ width: 26, height: 26, backgroundColor: t.warningBackground }}>
              <X size={14} style={{ color: t.warningText }} />
            </div>
            <div className="flex flex-col">
              <span className="text-[13px] font-bold" style={{ color: t.text }}>{PROVIDER_LABEL[failedProvider]}</span>
              <span className="text-[11px]" style={{ color: t.warningText }}>
                Failed — your prospect may not see this on {PROVIDER_LABEL[failedProvider]}
              </span>
            </div>
          </div>
        )}
      </div>

      {retryError && (
        <div className="w-full">
          <InlineErrorBanner error={retryError} t={t} />
        </div>
      )}
    </div>
  )
}

export default React.memo(BookMeetingModal)
